/**
 * Per-tab, single-use nonce that binds an OAuth authorize round-trip to the browser that started it.
 *
 * The nonce is placed in the authorize URL's `state`, the callback page echoes it forward, and the
 * page that consumes the authorization code only connects when the echoed value matches the stored
 * one. A code injected into a URL by someone else cannot carry a nonce the victim's tab generated.
 */

const NONCE_BYTES = 16;

export const createOAuthNonce = (): string => {
  const bytes = new Uint8Array(NONCE_BYTES);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Returns the nonce already stored for `key`, or stores a new one. Reusing the stored value keeps
 * repeated renders (and React StrictMode double invocation) on the same nonce.
 */
export const getOrCreateOAuthNonce = (key: string): string => {
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;

    const nonce = createOAuthNonce();
    sessionStorage.setItem(key, nonce);
    return nonce;
  } catch {
    // Storage blocked: return an unstored nonce so rendering never throws. consumeOAuthNonce then fails closed.
    return createOAuthNonce();
  }
};

/**
 * Removes the stored nonce for `key` and reports whether `returned` matches it. Always single-use:
 * the stored value is cleared whether or not it matches. Fails closed when storage is unavailable.
 */
export const consumeOAuthNonce = (key: string, returned: string | null | undefined): boolean => {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
  } catch {
    return false;
  }

  return Boolean(stored) && Boolean(returned) && stored === returned;
};
